import { useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import { Drawer } from '@mantine/core'
import {
  AlertTriangle,
  Camera,
  Check,
  ChevronRight,
  FolderOpen,
  Image as ImageIcon,
} from 'lucide-react'

import IconSparkle from 'src/icons/IconSparkle'

import type {
  CdlAutofillStatus,
  CdlScanSource,
} from '../hooks/useDriverCdlAutofill'

// Accept CDL photos (library / browse) and PDFs. The camera input below is a
// separate element with `capture` so "Take photo" opens the OS camera directly
// (with its native preview / retake), while these two open the picker.
const ACCEPT = 'image/*,application/pdf'

// Progress-less "uploading" card, matching the mockup's up-card. The real
// upload progress is not surfaced (S3 PUT gives no incremental events), so this
// is an indeterminate reassurance while bytes go up.
const UploadingCard = () => (
  <div className="mb-4 flex items-center gap-3 rounded-lg border border-doubleNickel-gray-200 bg-white p-3">
    <div className="h-9 w-12 flex-none rounded-md border border-doubleNickel-gray-200 bg-doubleNickel-gray-50" />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-doubleNickel-gray-900">
        CDL — front
      </div>
      <div className="mt-1 text-xs font-semibold text-doubleNickel-brand-600">
        Uploading…
      </div>
    </div>
  </div>
)

interface CdlScanBannerProps {
  status: CdlAutofillStatus
  // `source` is the sheet entry the driver used (RUM route dimension, ENG-4304).
  onFileSelected: (file: File, source: CdlScanSource) => void
  // Clears autofill state and returns the banner to idle (Rescan / try again).
  onReset: () => void
  // RUM returning_applicant dimension (ENG-4362) for the scan_opened emit.
  returningApplicant: boolean
}

/**
 * Mobile-only "Scan your CDL front" banner + upload source sheet for the driver
 * License Information step. Handles its own idle / uploading / analyzing /
 * filled / rejected / error presentation; the parent owns the actual autofill
 * orchestration (see useDriverCdlAutofill), the field prefill, and the
 * full-screen takeovers for 'analyzing' and 'unreadable'.
 */
const CdlScanBanner = ({
  status,
  onFileSelected,
  onReset,
  returningApplicant,
}: CdlScanBannerProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  // Which sheet button opened the current input. Both picker entries share one
  // <input>, so the button (not the input) is the only place the route is known.
  const sourceRef = useRef<CdlScanSource>('photo_library')

  // Intent marker (ENG-4362): fires when a sheet option launches the OS
  // camera/picker — before any file exists. The gap to scan_started is the
  // drivers who backed out of the OS UI (permission prompt, camera cancel).
  const trackOpened = (route: CdlScanSource) => {
    datadogRum.addAction('driver_cdl_scan_opened', {
      component: 'CdlScanBanner',
      route,
      returning_applicant: returningApplicant,
    })
  }

  const openPicker = (source: CdlScanSource) => {
    trackOpened(source)
    sourceRef.current = source
    setSheetOpen(false)
    // Defer so the drawer close doesn't swallow the input click on mobile.
    setTimeout(() => fileInputRef.current?.click(), 0)
  }

  // "Take photo": capture="environment" makes mobile browsers open the rear
  // camera directly; the OS camera supplies the preview / retake / use-photo
  // screens (no custom in-app camera, per the plan). On desktop browsers this
  // falls back to a file picker — fine, the flow is mobile-only.
  const openCamera = () => {
    trackOpened('take_photo')
    sourceRef.current = 'take_photo'
    setSheetOpen(false)
    setTimeout(() => cameraInputRef.current?.click(), 0)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input so re-picking the same file still fires onChange.
    e.target.value = ''
    if (file) onFileSelected(file, sourceRef.current)
  }

  // Hidden inputs shared by every state: picker (library / files) + camera.
  const hiddenInput = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
    </>
  )

  if (status === 'uploading') {
    return (
      <>
        <UploadingCard />
        {hiddenInput}
      </>
    )
  }

  // 'analyzing' is handled by LicenseScreen as a full-screen takeover, so the
  // banner renders nothing then (this component isn't mounted in that state).
  if (status === 'analyzing') {
    return hiddenInput
  }

  if (status === 'filled') {
    return (
      <>
        <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-doubleNickel-success-200 bg-doubleNickel-success-50 px-3 py-2.5">
          <span className="flex-none text-doubleNickel-success-700">
            <Check size={18} strokeWidth={2.2} />
          </span>
          <span className="flex-1 text-sm font-semibold text-doubleNickel-success-700">
            Filled from your CDL front
          </span>
          <button
            type="button"
            onClick={() => {
              // Back to the source sheet so the driver picks camera vs library.
              onReset()
              setSheetOpen(true)
            }}
            className="flex-none text-sm text-doubleNickel-brand-600 underline"
          >
            Rescan
          </button>
        </div>
        {hiddenInput}
      </>
    )
  }

  // 'unreadable' (a real CDL, bad photo) is handled by LicenseScreen as a
  // full-screen retake-first takeover (CdlRetakeContent, mockup Path 3) — the
  // banner never renders in that state. 'rejected' (not a CDL, e.g. a receipt)
  // and 'error' (upload / AI failure / timeout) stay inline: the fix for a
  // wrong document is picking the right one, not the takeover's photo-quality
  // tips. Error keeps this treatment until ENG-4313's clean manual fallback.
  if (status === 'rejected' || status === 'error') {
    return (
      <>
        <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-doubleNickel-warning-200 bg-doubleNickel-warning-50 px-3 py-2.5">
          <span className="mt-0.5 flex-none text-doubleNickel-warning-700">
            <AlertTriangle size={16} strokeWidth={2} />
          </span>
          <span className="flex-1 text-sm text-doubleNickel-warning-700">
            {status === 'rejected'
              ? "That didn't look like a CDL — try another photo or enter the details manually."
              : "We couldn't read your document — try again or enter the details manually."}
            <button
              type="button"
              onClick={() => {
                // Back to the source sheet so the driver picks camera vs library.
                onReset()
                setSheetOpen(true)
              }}
              className="ml-1 font-semibold text-doubleNickel-brand-600 underline"
            >
              Try again
            </button>
          </span>
        </div>
        {hiddenInput}
      </>
    )
  }

  // idle
  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="mb-4 flex w-full items-center gap-3 rounded-lg border border-doubleNickel-brand-100 bg-doubleNickel-brand-25 p-3 text-left"
      >
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-doubleNickel-brand-100 bg-white text-doubleNickel-brand-600">
          <IconSparkle size={18} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-doubleNickel-brand-700">
            Autofill from a photo
          </span>
          <span className="text-xs text-doubleNickel-brand-600">
            Scan your CDL front — skip the typing.
          </span>
        </span>
        <span className="flex-none text-doubleNickel-brand-600">
          <ChevronRight size={18} />
        </span>
      </button>

      <Drawer
        opened={sheetOpen}
        onClose={() => setSheetOpen(false)}
        position="bottom"
        withCloseButton={false}
        radius="16px 16px 0 0"
        classNames={{ body: 'p-0' }}
        // Hug the content: a bottom Drawer's `size` sets its height, so instead
        // of a fixed/oversized panel we let the content height drive it (capped)
        // — the sheet is only as tall as the options + Cancel, like the mockup.
        styles={{ content: { height: 'auto', maxHeight: '85vh' } }}
        title={null}
      >
        <div className="pb-2">
          <div className="mx-auto mb-1 mt-2 h-1 w-9 rounded-full bg-doubleNickel-gray-300" />
          <div className="border-b border-doubleNickel-gray-200 px-4 pb-2 pt-1 text-center text-xs font-semibold text-doubleNickel-gray-500">
            Scan your CDL front
          </div>
          <button
            type="button"
            onClick={openCamera}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-base text-doubleNickel-gray-900"
          >
            <Camera
              size={18}
              className="flex-none text-doubleNickel-brand-600"
            />
            Take photo
          </button>
          <button
            type="button"
            onClick={() => openPicker('photo_library')}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-base text-doubleNickel-gray-900"
          >
            <ImageIcon
              size={18}
              className="flex-none text-doubleNickel-brand-600"
            />
            Photo Library
          </button>
          <button
            type="button"
            onClick={() => openPicker('browse_files')}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-base text-doubleNickel-gray-900"
          >
            <FolderOpen
              size={18}
              className="flex-none text-doubleNickel-brand-600"
            />
            Browse Files
          </button>
          <button
            type="button"
            onClick={() => setSheetOpen(false)}
            className="mt-1.5 w-full border-t border-doubleNickel-gray-200 px-4 py-3 text-center text-base font-bold text-doubleNickel-brand-600"
          >
            Cancel
          </button>
        </div>
      </Drawer>
      {hiddenInput}
    </>
  )
}

export default CdlScanBanner
